import { FC, useEffect, useRef, useState } from 'react';
import cs from 'classnames';
import { Spin } from 'antd';
import { debounce, isArray } from '@lhb/func';
import { useMethods } from '@lhb/hook';
import IconFont from '@/common/components/IconFont';
import V2Message from '@/common/components/Others/V2Hint/V2Message';
import { keywordPrompt } from '@/common/utils/map';
import { rulers, RulerType, MeasureRulerType } from '../../ts-config';
import styles from './index.module.less';

// 联想搜索状态机
type InputStatus = 'idle' | 'searching' | 'success' | 'no_result' | 'timeout'; // 目前用到了'success' | 'no_result' | 'timeout'
// 输入框字段
type FieldType = 'start' | 'end';
// 选中的 POI
interface PoiItem {
  name: string;
  lng: number;
  lat: number;
  address: string;
}

/**
 * @Description 测距工具「输入」模式弹框
 * 用户通过 POI 联想搜索输入起终点地址，完成 直线/步行/驾车/骑行 测距
 */
interface Props {
  _mapIns: any;
  city?: { name?: string };
  floatDirection?: 'left' | 'right';
  rulerType: MeasureRulerType; // 当前测距类型（由 ToolBox 提升至父级管理）
  setRulerType: (type: MeasureRulerType) => void; // 更新测距类型；兼容 useState 的 Dispatch
  onClose: () => void;
  onMeasure: (params: {
    start: [number, number];
    end: [number, number];
    startAddress: string;
    endAddress: string;
    rulerType: MeasureRulerType;
  }) => void;
}

// 联想搜索 3s 超时
const SEARCH_TIMEOUT = 3000;
// 联想最多展示 8 条
const MAX_TIPS = 8;
// 防抖 300ms
const DEBOUNCE_MS = 300;

const RulerInputPanel: FC<Props> = ({
  _mapIns,
  city,
  floatDirection = 'right',
  rulerType,
  setRulerType,
  onClose,
  onMeasure,
}) => {
  // 顶部展示的测距类型按钮（过滤掉「输入」自身）
  const visibleRulers = rulers.filter((r) => r.type !== RulerType.Input);

  // 起终点 POI
  const [startPoi, setStartPoi] = useState<PoiItem | null>(null);
  const [endPoi, setEndPoi] = useState<PoiItem | null>(null);
  // 输入框文本（受控）
  const [startKw, setStartKw] = useState<string>('');
  const [endKw, setEndKw] = useState<string>('');
  // 当前 focus 的字段
  const [focusedField, setFocusedField] = useState<FieldType | null>(null);
  // 联想结果列表
  const [tips, setTips] = useState<any[]>([]);
  // 联想状态机
  const [inputStatus, setInputStatus] = useState<InputStatus>('idle');
  // 测距中（异步路径规划期间）：用于禁用类型按钮，避免快速切换产生竞态
  const [measuring, setMeasuring] = useState<boolean>(false);

  // 请求序号，旧请求结果根据 seq 比对丢弃（回答 🅓：旧处理停止）
  const searchSeqRef = useRef<number>(0);
  // 3s 超时计时器
  const timeoutRef = useRef<any>(null);
  // methods 最新引用，供 debounce 闭包使用
  const methodsRef = useRef<any>(null);
  // 防抖搜索函数，仅初始化一次
  const debouncedSearchRef = useRef<any>(null);
  // 起终点输入框 DOM 引用，用于选中 POI 后程序化 blur
  const startInputRef = useRef<HTMLInputElement>(null);
  const endInputRef = useRef<HTMLInputElement>(null);

  // 卸载时清理 3s 超时计时器（state 由组件 unmount/remount 自然重置，无需手动 reset）
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  // 插件可用性检测（仅 mount 时一次）
  useEffect(() => {
    if (!window?.AMap?.AutoComplete) {
      V2Message.error('地图联想插件未加载，无法使用「输入」测距');
    }
  }, []);

  // 入口未指定测距类型时兜底为直线，保证 InputPanel 总有一个高亮的类型按钮
  useEffect(() => {
    if (!rulerType) {
      setRulerType(MeasureRulerType.Line);
    }
  }, []);

  const methods = useMethods({
    // 选中起终点后/类型切换后的实际测距触发
    async handleMeasureWith(start: PoiItem, end: PoiItem, type: MeasureRulerType) {
      // 起终点经纬度完全相等
      if (start.lng === end.lng && start.lat === end.lat) {
        V2Message.warning('起终点相同，请重新输入');
        return;
      }
      setMeasuring(true);
      try {
        await onMeasure({
          start: [start.lng, start.lat],
          end: [end.lng, end.lat],
          startAddress: start.name,
          endAddress: end.name,
          rulerType: type,
        });
      } finally {
        setMeasuring(false);
      }
    },
    // 切换测距类型，已有起终点则立即重画
    handleTypeChange(type: MeasureRulerType) {
      if (measuring) return; // 测距进行中拦截，避免快速切换产生竞态
      if (type === rulerType) return;
      setRulerType(type);
      if (startPoi && endPoi) {
        methods.handleMeasureWith(startPoi, endPoi, type);
      }
    },
    /**
     * @description 将poi列表中地图可视范围内的数据提到最前面
     * @param list 接口返回的poi列表
     * @return
     */
    sortByViewport(list: any[]) {
      try {
        const bounds = _mapIns?.getBounds?.();
        if (!bounds || typeof bounds.contains !== 'function') return list;
        const inView: any[] = [];
        const outView: any[] = [];
        list.forEach((item: any) => {
          if (bounds.contains([+item.lng, +item.lat])) inView.push(item);
          else outView.push(item);
        });
        return inView.concat(outView);
      } catch (e) {
        return list;
      }
    },
    // 联想搜索实际执行（被防抖包裹后调用）
    doSearch(kw: string) {
      if (!kw) {
        setTips([]);
        setInputStatus('idle');
        return;
      }
      if (!window?.AMap?.AutoComplete) {
        setTips([]);
        setInputStatus('no_result');
        return;
      }
      searchSeqRef.current += 1;
      const mySeq = searchSeqRef.current;
      setInputStatus('searching');
      // 启动 3s 超时
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        // 已被新请求覆盖时不再处理
        if (mySeq !== searchSeqRef.current) return;
        setTips([]);
        setInputStatus('timeout');
      }, SEARCH_TIMEOUT);
      // 联想接口
      keywordPrompt(kw, {
        city: city?.name || undefined,
      }).then(
        (result: any) => {
          if (mySeq !== searchSeqRef.current) return; // 旧请求结果丢弃
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }
          const list = isArray(result) ? result : [];
          if (!list.length) {
            setTips([]);
            setInputStatus('no_result');
            return;
          }
          // 路 B 排序：视口内优先 + 视口外补足，截断到 8 条
          const sorted = methods.sortByViewport(list);
          setTips(sorted.slice(0, MAX_TIPS));
          setInputStatus('success');
        },
        () => {
          if (mySeq !== searchSeqRef.current) return;
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }
          setTips([]);
          setInputStatus('no_result');
        }
      );
    },
    // 输入框内容变更
    handleInputChange(field: FieldType, value: string) {
      if (field === 'start') {
        setStartKw(value);
        // 已选 POI 被改 → 视为重新选择
        if (startPoi && value !== startPoi.name) {
          setStartPoi(null);
        }
      } else {
        setEndKw(value);
        if (endPoi && value !== endPoi.name) {
          setEndPoi(null);
        }
      }
      // 立即清掉旧 tips，避免跨字段串扰
      setTips([]);
      // 空 value 直接 idle，避免 dropdown 短暂闪烁 searching 后再变 idle
      setInputStatus(value ? 'searching' : 'idle');
      // 触发防抖搜索
      debouncedSearchRef.current?.(value);
    },
    // 输入框获取焦点
    handleFocus(field: FieldType) {
      setFocusedField(field);
      // 已有关键字 → 走 debounce 重搜，防止用户高频切换两个输入框导致重复请求
      const kw = field === 'start' ? startKw : endKw;
      if (kw) {
        setTips([]);
        setInputStatus('searching');
        debouncedSearchRef.current?.(kw);
      }
    },
    // 输入框失去焦点
    handleBlur() {
      setFocusedField(null);
    },
    // 选中联想项
    handleSelectTip(field: FieldType, tip: any) {
      if (measuring) return;
      const poi: PoiItem = {
        name: tip.name,
        lng: +tip.lng,
        lat: +tip.lat,
        address: tip.address || '',
      };
      if (field === 'start') {
        setStartPoi(poi);
        setStartKw(poi.name);
      } else {
        setEndPoi(poi);
        setEndKw(poi.name);
      }
      setTips([]);
      setInputStatus('idle');
      // 程序化 blur 当前输入框：触发原生 onBlur → handleBlur → focusedField=null
      // 让 state 与 DOM focus 保持一致，× 与 dropdown 自然隐藏
      (field === 'start' ? startInputRef : endInputRef).current?.blur();
      // 起终点齐全 → 立即测距
      const fullStart = field === 'start' ? poi : startPoi;
      const fullEnd = field === 'end' ? poi : endPoi;
      if (fullStart && fullEnd) {
        methods.handleMeasureWith(fullStart, fullEnd, rulerType);
      }
    },
    // 清空输入框
    handleClear(field: FieldType) {
      if (field === 'start') {
        setStartKw('');
        setStartPoi(null);
      } else {
        setEndKw('');
        setEndPoi(null);
      }
      setTips([]);
      setInputStatus('idle');
    },
    // 关闭弹框
    handleClose() {
      onClose();
    },
    // 渲染下拉区
    renderDropdown() {
      if (!focusedField) return null;
      const kw = focusedField === 'start' ? startKw : endKw;
      if (!kw) return null;
      // 搜索中
      if (inputStatus === 'searching') {
        return (
          <div className={styles.dropdown}>
            <div className={styles.emptyHint}>
              <Spin size='small' /> 加载中...
            </div>
          </div>
        );
      }
      // 未找到
      if (inputStatus === 'no_result') {
        return (
          <div className={styles.dropdown}>
            <div className={styles.emptyHint}>未找到相关地址，请重新输入</div>
          </div>
        );
      }
      // 超时
      if (inputStatus === 'timeout') {
        return (
          <div className={styles.dropdown}>
            <div className={styles.emptyHint}>搜索超时，请检查网络</div>
          </div>
        );
      }
      if (inputStatus !== 'success' || !tips.length) return null;
      return (
        <div className={styles.dropdown}>
          {tips.map((tip, idx) => (
            <div
              key={`${tip.id || tip.name}-${idx}`}
              className={styles.tipItem}
              // onMouseDown + preventDefault：在 blur 前触发，保证 click 能注册到
              onMouseDown={(e) => {
                e.preventDefault();
                methods.handleSelectTip(focusedField, tip);
              }}
            >
              <div className={styles.tipName}>{tip.name}</div>
              <div className={styles.tipAddress}>{tip.address}</div>
            </div>
          ))}
        </div>
      );
    },
  });

  // 维护 methodsRef 最新引用（供 debounce 闭包读取）
  methodsRef.current = methods;
  // 防抖函数仅初始化一次，确保多次输入复用同一计时器
  if (!debouncedSearchRef.current) {
    debouncedSearchRef.current = debounce((kw: string) => {
      methodsRef.current?.doSearch(kw);
    }, DEBOUNCE_MS);
  }

  // 红边仅当前 focus 字段 + no_result 状态
  const isStartError = focusedField === 'start' && inputStatus === 'no_result';
  const isEndError = focusedField === 'end' && inputStatus === 'no_result';

  return (
    <div className={cs(styles.inputPanel, floatDirection === 'left' && styles.inputPanelLeft)}>
      {/* 顶部：4 个测距类型按钮 + 关闭按钮 */}
      <div className={styles.inputPanelHeader}>
        <div className={styles.typeRow}>
          {visibleRulers.map((item) => (
            <div
              key={item.type}
              className={cs(
                styles.typeBtn,
                rulerType === item.type && styles.typeBtnActive,
                measuring && styles.typeBtnDisabled
              )}
              onClick={() => methods.handleTypeChange(item.type as MeasureRulerType)}
            >
              <IconFont iconHref={item.icon} />
            </div>
          ))}
        </div>
        <div className={styles.closeBtn} onClick={methods.handleClose}>
          <IconFont iconHref='iconic_close_colour_seven' />
        </div>
      </div>

      {/* 输入区 */}
      <div className={styles.inputAreaWrapper}>
        <div className={styles.inputArea}>
          {/* 起点 */}
          <div className={styles.inputRow}>
            <span className={cs(styles.dot, styles.dotStart)} />
            <input
              ref={startInputRef}
              className={cs(styles.input, isStartError && styles.inputError)}
              placeholder='请输入起点'
              value={startKw}
              onChange={(e) => methods.handleInputChange('start', e.target.value)}
              onFocus={() => methods.handleFocus('start')}
              onBlur={methods.handleBlur}
            />
            {focusedField === 'start' && !!startKw && (
              <div
                className={styles.clearBtn}
                onMouseDown={(e) => {
                  e.preventDefault();
                  methods.handleClear('start');
                }}
              >
                <IconFont iconHref='iconic_close_colour_seven'/>
              </div>
            )}
          </div>
          <div className={styles.inputDivider} />
          {/* 终点 */}
          <div className={styles.inputRow}>
            <span className={cs(styles.dot, styles.dotEnd)} />
            <input
              ref={endInputRef}
              className={cs(styles.input, isEndError && styles.inputError)}
              placeholder='请输入终点'
              value={endKw}
              onChange={(e) => methods.handleInputChange('end', e.target.value)}
              onFocus={() => methods.handleFocus('end')}
              onBlur={methods.handleBlur}
            />
            {focusedField === 'end' && !!endKw && (
              <div
                className={styles.clearBtn}
                onMouseDown={(e) => {
                  e.preventDefault();
                  methods.handleClear('end');
                }}
              >
                <IconFont iconHref='iconic_close_colour_seven'/>
              </div>
            )}
          </div>
          {/* 下拉联想列表 */}
          {methods.renderDropdown()}
        </div>
      </div>
    </div>
  );
};

export default RulerInputPanel;
